import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { useAuth } from '../../context/AuthContext';
import {
  Building2,
  Users,
  Activity,
  Shield,
  TrendingUp,
  AlertTriangle,
  Plus,
  Settings,
  BarChart3,
  Eye,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminDashboard() {
  const { hasRole } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newOrg, setNewOrg] = useState({
    name: '',
    domain: '',
    user_limit: 100,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analyticsRes, orgsRes] = await Promise.all([
        axios.get(`${API}/analytics/dashboard/platform`),
        axios.get(`${API}/organizations`),
      ]);
      setAnalytics(analyticsRes.data);
      setOrganizations(orgsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrg = async () => {
    if (!newOrg.name || !newOrg.domain) return;
    
    try {
      setCreating(true);
      await axios.post(`${API}/organizations`, newOrg);
      setShowCreateDialog(false);
      setNewOrg({ name: '', domain: '', user_limit: 100 });
      fetchData();
    } catch (error) {
      console.error('Failed to create organization:', error);
      toast.error(error.response?.data?.detail || 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  if (!hasRole(['superadmin', 'platform_admin'])) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-slate-500">You don't have permission to view this page.</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin w-8 h-8 border-4 border-sky-500 border-t-transparent rounded-full" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="superadmin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Platform Administration</h1>
            <p className="text-slate-500">Manage organizations, users, and platform settings</p>
          </div>
          <div className="flex gap-3">
            <Button onClick={() => setShowCreateDialog(true)} data-testid="create-org-btn">
              <Plus className="w-4 h-4 mr-2" />
              New Organization
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card data-testid="stat-total-orgs">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Organizations</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.total_organizations || 0}</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-sky-100 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-sky-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-total-users">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Users</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.total_users || 0}</p>
                  <p className="text-xs text-green-600 mt-1">
                    {analytics?.active_users || 0} active
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <Users className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-dau">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Daily Active Users</p>
                  <p className="text-3xl font-bold text-slate-900">{analytics?.dau || 0}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    MAU: {analytics?.mau || 0}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="stat-security">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Security Events</p>
                  <p className="text-3xl font-bold text-slate-900">
                    {analytics?.recent_security_events?.length || 0}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">Last 24h</p>
                </div>
                <div className="w-12 h-12 rounded-lg bg-red-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Organizations List */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Organizations</CardTitle>
                  <CardDescription>All registered organizations</CardDescription>
                </div>
                <Link to="/admin/organizations">
                  <Button variant="outline" size="sm">
                    <Eye className="w-4 h-4 mr-2" />
                    View All
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {organizations.slice(0, 5).map((org) => (
                    <div
                      key={org.id}
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                      data-testid={`org-${org.id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-sky-500 flex items-center justify-center text-white font-bold">
                          {org.name.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{org.name}</p>
                          <p className="text-sm text-slate-500">{org.domain}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={org.status === 'active' ? 'default' : 'secondary'}>
                          {org.status}
                        </Badge>
                        <div className="text-sm text-slate-500">
                          {org.enabled_modules?.length || 0} modules
                        </div>
                        <Link to={`/admin/organizations/${org.id}`}>
                          <Button variant="ghost" size="sm">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                  {organizations.length === 0 && (
                    <p className="text-center text-slate-500 py-8">No organizations yet</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Module Adoption */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Module Adoption</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics?.module_adoption || {}).map(([module, count]) => (
                    <div key={module} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 capitalize">{module.replace('_', ' ')}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-sky-500 rounded-full"
                            style={{ width: `${(count / (analytics?.total_organizations || 1)) * 100}%` }}
                          />
                        </div>
                        <span className="text-sm font-medium text-slate-900 w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Role Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(analytics?.role_distribution || {}).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <span className="text-sm text-slate-600 capitalize">{role.replace('_', ' ')}</span>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/admin/users" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Users className="w-4 h-4 mr-2" />
                    Manage Users
                  </Button>
                </Link>
                <Link to="/admin/analytics" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <BarChart3 className="w-4 h-4 mr-2" />
                    View Analytics
                  </Button>
                </Link>
                <Link to="/admin/security" className="block">
                  <Button variant="outline" className="w-full justify-start">
                    <Shield className="w-4 h-4 mr-2" />
                    Security Settings
                  </Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create Organization Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="sm:max-w-[425px]">
            <DialogHeader>
              <DialogTitle>Create New Organization</DialogTitle>
              <DialogDescription>
                Add a new organization to the platform. You can configure modules and settings later.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="org-name">Organization Name *</Label>
                <Input
                  id="org-name"
                  value={newOrg.name}
                  onChange={(e) => setNewOrg({ ...newOrg, name: e.target.value })}
                  placeholder="Acme Energy Corp"
                  data-testid="org-name-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="org-domain">Domain *</Label>
                <Input
                  id="org-domain"
                  value={newOrg.domain}
                  onChange={(e) => setNewOrg({ ...newOrg, domain: e.target.value })}
                  placeholder="acme-energy.com"
                  data-testid="org-domain-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="org-limit">User Limit</Label>
                <Input
                  id="org-limit"
                  type="number"
                  value={newOrg.user_limit}
                  onChange={(e) => setNewOrg({ ...newOrg, user_limit: parseInt(e.target.value) || 100 })}
                  data-testid="org-limit-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateOrg} 
                disabled={creating || !newOrg.name || !newOrg.domain}
                data-testid="submit-org-btn"
              >
                {creating ? 'Creating...' : 'Create Organization'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
